import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Zap } from "lucide-react";
import { useApp } from "../context/AppContext";
import { Endpoints } from "../lib/api";
import { dayTimeOptions } from "../lib/time";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Dialog } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Select } from "../components/ui/select";

export default function AbsencesPage() {
  const { config, absences, agents, refreshLists, setSchedule } = useApp();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const agentName = Object.fromEntries(agents.map((a) => [a.id, a.name]));
  const mkEmpty = () => ({
    agent_id: agents[0]?.id || "",
    day: config?.days?.[1]?.date || config?.days?.[0]?.date || "",
    start_time: "13:00", end_time: "16:00", reason: "Unplanned",
  });
  const [form, setForm] = useState(mkEmpty());

  const create = async () => {
    setBusy(true);
    try {
      const res = await Endpoints.createAbsence({
        agent_id: form.agent_id,
        start_datetime: `${form.day}T${form.start_time}`,
        end_datetime: `${form.day}T${form.end_time}`,
        reason: form.reason,
      });
      if (res.schedule) setSchedule(res.schedule);
      await refreshLists();
      setOpen(false);
      toast.success("Absence logged — schedule recalculated", {
        description: `Re-optimised around ${agentName[form.agent_id]}'s absence.`,
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    const res = await Endpoints.deleteAbsence(id);
    if (res.schedule) setSchedule(res.schedule);
    await refreshLists();
    toast.success("Absence cleared — schedule recalculated");
  };

  const dayOptions = (config?.days || []).map((d) => ({ value: d.date, label: `${d.label} · ${d.date.slice(5)}` }));
  const timeOptions = dayTimeOptions(config);

  return (
    <div className="animate-fadeup">
      <PageHeader title="Absences" subtitle="Log an unavailability on any day. Each change instantly re-runs the solver to absorb the disruption.">
        <Button variant="primary" size="sm" onClick={() => { setForm(mkEmpty()); setOpen(true); }} data-testid="add-absence-btn">
          <Plus className="w-4 h-4" /> Log absence
        </Button>
      </PageHeader>

      <Card className="mb-4">
        <CardContent className="flex items-start gap-3 pt-4">
          <Zap className="w-4 h-4 text-warning mt-0.5 shrink-0" />
          <p className="text-xs text-muted leading-relaxed">
            Logging an absence triggers a full weekly <span className="text-ink">dynamic recalculation</span>. Use{" "}
            <span className="text-ink">Freeze &amp; recalc</span> in the top bar to additionally lock every slot before a
            chosen day/time (e.g. everything already worked) so only future slots are re-planned.
          </p>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-line text-[11px] uppercase tracking-wider text-muted">
              <Th>Agent</Th><Th>From</Th><Th>To</Th><Th>Reason</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {absences.length === 0 && <tr><Td className="text-muted" colSpan={5}>No absences logged.</Td></tr>}
            {absences.map((ab) => (
              <tr key={ab.id} className="border-b border-line last:border-0 hover:bg-white/5 transition-colors duration-100" data-testid={`absence-row-${ab.id}`}>
                <Td className="text-ink font-medium">{agentName[ab.agent_id] || ab.agent_id}</Td>
                <Td className="mono text-muted">{ab.start_datetime.replace("T", " ")}</Td>
                <Td className="mono text-muted">{ab.end_datetime.replace("T", " ")}</Td>
                <Td className="text-muted">{ab.reason}</Td>
                <Td className="text-right">
                  <Button variant="danger" size="icon" onClick={() => remove(ab.id)} data-testid={`delete-absence-${ab.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} title="Log absence" description="Triggers an immediate weekly re-optimisation." testid="absence-dialog"
        footer={<><Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={create} disabled={busy} data-testid="save-absence-btn">
            {busy ? "Recalculating…" : "Log & recalculate"}
          </Button></>}>
        <Select label="Agent" value={form.agent_id} onChange={(v) => setForm({ ...form, agent_id: v })}
          options={agents.map((a) => ({ value: a.id, label: a.name }))} testid="absence-agent-select" />
        <Select label="Day" value={form.day} onChange={(v) => setForm({ ...form, day: v })} options={dayOptions} testid="absence-day-select" />
        <div className="grid grid-cols-2 gap-3">
          <Select label="From" value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} options={timeOptions} testid="absence-start-select" />
          <Select label="To" value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} options={timeOptions} testid="absence-end-select" />
        </div>
        <Input label="Reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="absence-reason-input" />
      </Dialog>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 font-medium">{children}</th>;
const Td = ({ children, className = "", colSpan }) => <td colSpan={colSpan} className={`px-4 py-3 ${className}`}>{children}</td>;
